import { Button, Divider, FormControl, Modal, Stack, Typography } from "@mui/material";
import { GridCallbackDetails, GridSelectionModel } from "@mui/x-data-grid";
import React, { useContext, useState } from "react";

import IconNewCircle from "../../../../assets/images/icons/icon_new_circle.svg";
import DataGrid from "../../../../components/DataGrid/DataGrid";
import ImgIcon from "../../../../components/ImgIcon/ImgIcon";
import ProgressModal from "../../../../components/Progress/ProgressModal/ProgressModal";
import { AppContext } from "../../../../core/context/appContextProvider";
import { getClientTransactionMapping } from "../../../../services/client.service";
import { getTransactionBuckets } from "../../../../services/fund.service";
import { useEffectAsync } from "../../../../utils/hooks/useEffectAsync.hook";
import { ClientTransTypeRow } from "../../../../utils/types/clientTransMapping.type";
import { TransTypeBucket } from "../../../../utils/types/fund.type";
import { Workbook } from "../../../../utils/types/workbook.type";
import NewTransactionModal from "../../transactionMapper/NewTransactionModal/NewTransactionModal";
import { ScrollingStyledContainer, StyledBox } from "./NewTransactionTypeSelector.styles";

type NewTransactionTypeSelectorProps = {
    workbook: Workbook,
    onSave: (selectedTypes: ClientTransTypeRow[]) => void,
    onCancel: () => void
};

export const NewTransactionTypeSelector: React.FC<NewTransactionTypeSelectorProps> = ({
  workbook, onSave, onCancel 
}) => {
  const {
    state 
  } = useContext(AppContext);
  const {
    clientId 
  } = state.loginUser;

  const [isLoading, setIsloading] = useState(true);
  const [clientTransTypeRows, setClientTransTypeRows] = useState<ClientTransTypeRow[]>([]);
  const [selectedClientTransTypeRows, setSelectedClientTransTypeRows] = useState<ClientTransTypeRow[]>([]);
  const [transactionBuckets, setTransactionBuckets] = useState<TransTypeBucket[]>([]);
  const [showNewTransactionDialog, setShowNewTransactionDialog] = useState(false);
  const [refreshData, setRefreshData] = useState(false);

  useEffectAsync(async (isCanceled) => {
    const response = await getClientTransactionMapping(clientId);

    if (isCanceled()) return;

    const transBucketsResponse = await getTransactionBuckets(clientId);

    if (isCanceled()) return;

    const filteredTransTypes = response.rows.filter((transCol) => workbook.commitmentTransactionTypeIds.find((typeID) => typeID === transCol.id) === undefined);
    const transTypeRows = filteredTransTypes.filter((row) => workbook.transactionColumns.find((stt) => stt.transTypeId === row.id) === undefined);

    setClientTransTypeRows(transTypeRows);
    setTransactionBuckets(transBucketsResponse);

    setIsloading(false);
  }, [refreshData]);


  function handleCreateNewTransactionType() {
    setShowNewTransactionDialog(true);
  }

  function handleSaveNewTransactionType() {
    setRefreshData(!refreshData);
    setShowNewTransactionDialog(false);
  }

  function handleAddTransactionColumn() {
    onSave(selectedClientTransTypeRows);
    setShowNewTransactionDialog(false);
  }


  function handleSelectionModelChange(selectionModel: GridSelectionModel, _details: GridCallbackDetails) {
    const selected = clientTransTypeRows.filter((row) => selectionModel.find((sm) => sm === row.id));

    setSelectedClientTransTypeRows(selected);
  }

  const headerList:any[] =
  [
    {
      field: "type",
      headerName: "Transaction Column",
      hide: false,
      index: 1,
      type: "string",
      sortable: false,
      align: "left",
      width: "250px"
    }
  ];


  return (
    <>
      <Modal
        open={true}
        onClose={onCancel}
        id="mdl_new_transaction"
        aria-labelledby="modal_newtranstype_title"
        aria-describedby="modal_newtranstype_description"
      >
        <StyledBox>
          <Typography id="modal_newtranstype_title" component="h2">
            Create Custom Ark Transaction Type
          </Typography>
          <Typography id="modal_newtranstype_description" variant='body2'>
            TODO: Need verbage here to desribe whats going on
          </Typography>

          <Divider />

          <ScrollingStyledContainer>
            <DataGrid
              id="data_grid"
              onSelectionModelChange={handleSelectionModelChange}
              dataList={clientTransTypeRows}
              headerList={headerList}
              checkboxSelection={true}
              minHeight="100px"
            />
          </ScrollingStyledContainer>

          <FormControl>

            <Stack direction="row" spacing={4} sx={{
              mt: 3 
            }}>
              <Typography variant="subtitle1">Create new Transaction Type</Typography>
              <ImgIcon
                icon={IconNewCircle}
                useCursorPointer={true}
                onClick={() => handleCreateNewTransactionType()}
              />
            </Stack>

            <Stack direction="row" spacing={4} sx={{
              mt: 3 
            }}>
              <Button
                id="btn_save_new_transtype"
                variant="contained"
                onClick={handleAddTransactionColumn}
                disabled={selectedClientTransTypeRows.length === 0}
              >
                Add Transaction Column
              </Button>

              <Button id="btn_cancel_save" variant="outlined" onClick={onCancel}>Cancel</Button>
            </Stack>
          </FormControl>
        </StyledBox>
      </Modal>

      <NewTransactionModal
        clientId={clientId}
        open={showNewTransactionDialog}
        transTypeModel={transactionBuckets}
        onSuccess={handleSaveNewTransactionType}
        onClose={() => setShowNewTransactionDialog(false)}
      />

      <ProgressModal
        id="modal_saving_mapping"
        showProgress={isLoading}
        text="Finding new Transaction Types..."
      />
    </>
  );

};
